/**
 * Undo/redo for the chart canvas.
 *
 * Every entry knows the state BEFORE and the state AFTER the action it stands
 * for, and both directions are replayed through the same endpoints — and so
 * the same authorization — the original action used. Nothing is stored on the
 * server: the history belongs to this visit, and a reload starts it empty.
 */

import { useCallback, useMemo, useRef, useState } from 'react'
import type { SeatDetail } from './types'

/** A stored placement. Both null means "unplaced — let the tidy tree decide". */
export interface SeatCoordinates {
  id: number
  x: number | null
  y: number | null
}

/** Everything about where a box sits in the chart, coordinates included. */
export interface SeatPlacement {
  parentSeatId: number | null
  position: number
  x: number | null
  y: number | null
}

/**
 * Enough of a deleted box to put it back. Comments are absent on purpose, and
 * are not lost by it: deleting a box detaches its conversation on the server
 * rather than destroying it, so a restore re-attaches the original rows with
 * their own authors and timestamps instead of rebuilding them from here.
 */
export interface SeatSnapshot {
  name: string
  description: string | null
  seatStatus: string
  placement: SeatPlacement
  responsibilities: { title: string; description: string | null; position: number }[]
  /**
   * The ids those rows had before the box went, in the same order. A restore
   * builds NEW rows, so this is what an entry naming one of the old ones is
   * renumbered from.
   */
  responsibilityIds: number[]
  tagIds: number[]
  assignments: { user_id: number | null; external_name: string | null; is_primary: boolean }[]
  childSeatIds: number[]
}

/**
 * Which typed field an edit changed, and how to reach it again. The seat id is
 * carried even for a responsibility, because its endpoint is nested under the
 * box — and because a rebuilt box has to renumber every entry that names it.
 */
export type ChartFieldTarget =
  | { type: 'seat'; seatId: number; field: 'name' | 'description' | 'seat_status' }
  | { type: 'responsibility'; seatId: number; responsibilityId: number }

/**
 * A typed edit that has ALREADY been saved: the value the server held before
 * it and the value it holds now. Undo and redo are the same save again with
 * the other value, so they go through the same endpoint and the same policy.
 */
export interface ChartFieldEdit {
  label: string
  target: ChartFieldTarget
  before: string
  after: string
}

export type ChartHistoryEntry =
  | { id: number; kind: 'positions'; label: string; before: SeatCoordinates[]; after: SeatCoordinates[] }
  | { id: number; kind: 'move'; label: string; seatId: number; before: SeatPlacement; after: SeatPlacement }
  | { id: number; kind: 'delete'; label: string; seatId: number; snapshot: SeatSnapshot }
  /**
   * A box that was added or duplicated. It carries the same snapshot a
   * deletion does, because it is replayed by the same two requests in the
   * opposite order: undo deletes the new box, redo rebuilds it.
   */
  | { id: number; kind: 'create'; label: string; seatId: number; snapshot: SeatSnapshot }
  /**
   * A typed field that was edited and saved. Nothing is snapshotted: the two
   * values ARE the entry, and both directions are the original save again.
   */
  | ({ id: number; kind: 'field' } & ChartFieldEdit)

/**
 * An entry before the history gives it its number. Omit over a union has to be
 * distributed by hand, or TypeScript reduces the three shapes to their shared
 * keys and rejects every entry that actually carries a payload.
 */
export type NewChartHistoryEntry =
  ChartHistoryEntry extends infer T ? (T extends ChartHistoryEntry ? Omit<T, 'id'> : never) : never

/** How many actions back a visit can reach. */
export const HISTORY_LIMIT = 50

let nextEntryId = 1

export function newEntryId() {
  return nextEntryId++
}

export function placementOf(seat: SeatDetail): SeatPlacement {
  return {
    parentSeatId: seat.parent_seat_id,
    position: seat.position,
    x: seat.layout_x,
    y: seat.layout_y,
  }
}

export function coordinatesOf(seat: SeatDetail): SeatCoordinates {
  return { id: seat.id, x: seat.layout_x, y: seat.layout_y }
}

/** Everything needed to rebuild a box, read off the copy the page already has. */
export function snapshotSeat(seat: SeatDetail, allSeats: SeatDetail[]): SeatSnapshot {
  return {
    name: seat.name,
    description: seat.description,
    seatStatus: seat.seat_status,
    placement: placementOf(seat),
    responsibilities: seat.responsibilities.map(r => ({
      title: r.title,
      description: r.description,
      position: r.position,
    })),
    responsibilityIds: seat.responsibilities.map(r => r.id),
    tagIds: seat.tags.map(tag => tag.id),
    assignments: seat.assignments.map(a => ({
      user_id: a.user_id,
      external_name: a.external_name,
      is_primary: a.is_primary,
    })),
    childSeatIds: allSeats.filter(s => s.parent_seat_id === seat.id).map(s => s.id),
  }
}

interface HistoryState {
  past: ChartHistoryEntry[]
  future: ChartHistoryEntry[]
}

export interface ChartHistory {
  canUndo: boolean
  canRedo: boolean
  /** What Ctrl+Z would take back, and what Ctrl+Shift+Z would put back. */
  undoLabel: string | null
  redoLabel: string | null
  /** The entry each direction would replay, without committing to it. */
  peekUndo: () => ChartHistoryEntry | null
  peekRedo: () => ChartHistoryEntry | null
  record: (entry: NewChartHistoryEntry) => void
  /** Called once the server has accepted the replay, never before. */
  commitUndo: (entry: ChartHistoryEntry) => void
  commitRedo: (entry: ChartHistoryEntry) => void
  /**
   * A restored box is a NEW row. EVERY entry that mentions the old id has to
   * learn the new one — not just the deletion that put it back: an earlier
   * move or alignment still names the box it moved, and another box may report
   * to it.
   *
   * Its responsibilities are new rows too, so `responsibilityIds` maps the old
   * ones onto them. An entry about a responsibility that cannot be mapped is
   * forgotten: there is no row left to save its text back to, and leaving it
   * in place would jam undo against a row that is gone.
   */
  remapSeatId: (oldSeatId: number, newSeatId: number, responsibilityIds?: Record<number, number>) => void
  clear: () => void
}

export function useChartHistory(): ChartHistory {
  const [state, setState] = useState<HistoryState>({ past: [], future: [] })
  const stateRef = useRef(state)
  stateRef.current = state

  const record = useCallback((entry: NewChartHistoryEntry) => {
    setState(current => ({
      // A new action makes the redo stack unreachable: it described a future
      // that no longer follows from what is on screen.
      past: [...current.past, { ...entry, id: newEntryId() } as ChartHistoryEntry].slice(-HISTORY_LIMIT),
      future: [],
    }))
  }, [])

  const commitUndo = useCallback((entry: ChartHistoryEntry) => {
    setState(current => ({
      past: current.past.filter(candidate => candidate.id !== entry.id),
      future: [entry, ...current.future.filter(candidate => candidate.id !== entry.id)],
    }))
  }, [])

  const commitRedo = useCallback((entry: ChartHistoryEntry) => {
    setState(current => ({
      past: [...current.past.filter(candidate => candidate.id !== entry.id), entry].slice(-HISTORY_LIMIT),
      future: current.future.filter(candidate => candidate.id !== entry.id),
    }))
  }, [])

  const remapSeatId = useCallback((
    oldSeatId: number,
    newSeatId: number,
    responsibilityIds: Record<number, number> = {},
  ) => {
    if (oldSeatId === newSeatId) return
    // The new id is a row the chart has never had, so nothing else can already
    // be using it and the swap can be applied everywhere without checking.
    const swap = (id: number | null) => (id === oldSeatId ? newSeatId : id)
    /** Null for an entry that cannot survive the renumbering and is dropped. */
    const remap = (entry: ChartHistoryEntry): ChartHistoryEntry | null => {
      switch (entry.kind) {
        case 'positions':
          return {
            ...entry,
            before: entry.before.map(c => (c.id === oldSeatId ? { ...c, id: newSeatId } : c)),
            after: entry.after.map(c => (c.id === oldSeatId ? { ...c, id: newSeatId } : c)),
          }
        case 'move':
          return {
            ...entry,
            seatId: swap(entry.seatId) as number,
            // A box that reported to the deleted one has its parent renumbered
            // too, or undoing that move would point it at a row that is gone.
            before: { ...entry.before, parentSeatId: swap(entry.before.parentSeatId) },
            after: { ...entry.after, parentSeatId: swap(entry.after.parentSeatId) },
          }
        case 'delete':
        case 'create':
          return {
            ...entry,
            seatId: swap(entry.seatId) as number,
            snapshot: {
              ...entry.snapshot,
              placement: {
                ...entry.snapshot.placement,
                parentSeatId: swap(entry.snapshot.placement.parentSeatId),
              },
              childSeatIds: entry.snapshot.childSeatIds.map(id => (id === oldSeatId ? newSeatId : id)),
            },
          }
        case 'field': {
          const target = entry.target
          if (target.type === 'responsibility' && target.seatId === oldSeatId) {
            const rebuiltId = responsibilityIds[target.responsibilityId]
            // The rebuilt box's responsibilities are new rows. Without the one
            // this entry names there is nothing to put its text back on, so
            // the entry goes rather than jamming undo against a row that is
            // gone.
            if (!rebuiltId) return null
            return { ...entry, target: { ...target, seatId: newSeatId, responsibilityId: rebuiltId } }
          }
          return { ...entry, target: { ...target, seatId: swap(target.seatId) as number } }
        }
      }
    }
    const remapAll = (entries: ChartHistoryEntry[]) =>
      entries.map(remap).filter((entry): entry is ChartHistoryEntry => entry !== null)
    setState(current => ({
      past: remapAll(current.past),
      future: remapAll(current.future),
    }))
  }, [])

  const clear = useCallback(() => setState({ past: [], future: [] }), [])

  const peekUndo = useCallback(() => stateRef.current.past[stateRef.current.past.length - 1] ?? null, [])
  const peekRedo = useCallback(() => stateRef.current.future[0] ?? null, [])

  return useMemo(() => ({
    canUndo: state.past.length > 0,
    canRedo: state.future.length > 0,
    undoLabel: state.past[state.past.length - 1]?.label ?? null,
    redoLabel: state.future[0]?.label ?? null,
    peekUndo,
    peekRedo,
    record,
    commitUndo,
    commitRedo,
    remapSeatId,
    clear,
  }), [state, peekUndo, peekRedo, record, commitUndo, commitRedo, remapSeatId, clear])
}
